import { isLightColor, parseColor } from '@shared/utils/color';
import * as chroma from 'chroma-js';

/**
 * Determines if a string is in rgb or rgba format.  the alpha value is ignored if rgba.
 * @param r red channel value 0-255
 * @param g green channel value 0-255
 * @param g blue channel value 0-255
 * @returns object {r: number, g:number, b:number } or undefined if the string is not in rgb format.
 */
export const toRGBObject = (
  rgbStr: string,
):
  | {
      r: number;
      g: number;
      b: number;
    }
  | undefined => {
  // exit if input is not a non-zero length string
  if (!rgbStr || typeof rgbStr !== 'string') {
    return;
  }
  // match on 'rgb(' and 'rgba(' CSS color ))
  const match = /rgba?\(\s*(\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}).*/.exec(rgbStr);
  if (match) {
    const values = rgbStr.match(/\d+/g);
    // if we have an rgba string length may be 4.  We only care that length is at least 3
    if (values && values.length > 2) {
      const [red, green, blue] = values.map(Number);
      return {
        r: red,
        g: green,
        b: blue,
      };
    }
  }
  return;
};

/**
 * Determines if a string is in css hex format ( '#ffcc44' ).
 * @param color string to test
 * @returns boolean true if the color is in css hex format ( '#ffcc44' ).
 */
export const isHexColor = (color: string): boolean => {
  return /^#[0-9A-F]{6}$/i.test(color);
};

export const hexToRgb = (hexColor: string) => {
  const base = hexColor.startsWith('#') ? 1 : 0;

  const r = parseInt(hexColor.substring(base, 3), 16);
  const g = parseInt(hexColor.substring(base + 2, 5), 16);
  const b = parseInt(hexColor.substring(base + 4, 7), 16);

  return { r, g, b };
};

/**
 * @param color String representation of a color (as supported in CSS)
 * @param lightMultiplier darken multiplier value
 * @param darkMultiplier lighten multiplier value
 * @returns hex string value of the adjusted color
 */
export const shadeColor = (color: string, lightMultiplier: number, darkMultiplier: number): string => {
  const parsedColor = parseColor(color);

  if (!parsedColor || isNaN(lightMultiplier) || isNaN(darkMultiplier)) {
    return `rgb(220, 220, 220)`;
  }

  const rgb = parsedColor.rgb();
  const isLight = isLightColor(...rgb);

  if (isLight) {
    return chroma.mix(color, 'black', lightMultiplier).hex();
  }

  return chroma.mix(color, 'white', darkMultiplier).hex();
};
